import { useEffect, useState } from 'react'

/**
 * Connect's own published page index, fetched once per page load.
 *
 * THE SAME FILE THE AGENT NAVIGATES BY. It is generated from the nav
 * config (`scripts/generate-nav-manifest.ts`) and served as a static
 * asset, and trunk reads it to answer "where is that". So anything the
 * surface says about a page comes from the same source the agent would
 * use, and the two cannot disagree: a breadcrumb shown here is the
 * breadcrumb the agent would read out.
 *
 * CACHED AT MODULE LEVEL rather than per component. Two panels want it,
 * and a static file fetched twice on every open is two requests for one
 * answer.
 */

export interface NavManifestItem {
  id: string
  label: string
  breadcrumb: string[]
  path: string
  scope: 'org' | 'venue'
  gates?: string[]
}

let cache: NavManifestItem[] | null = null
let inFlight: Promise<NavManifestItem[]> | null = null

/** Tests, and anywhere the manifest has been regenerated under us. */
export function resetNavManifestCache(): void {
  cache = null
  inFlight = null
}

async function load(): Promise<NavManifestItem[]> {
  if (cache) return cache
  inFlight ??= fetch('/nav-manifest.json')
    .then(async (res) => {
      if (!res.ok) throw new Error(String(res.status))
      return (await res.json()) as { items: NavManifestItem[] }
    })
    .then((manifest) => {
      cache = manifest.items
      return cache
    })
    .catch((error: unknown) => {
      // Cleared so a later open can try again: a failed fetch should not
      // leave the panel permanently empty for the session.
      inFlight = null
      throw error
    })
  return inFlight
}

export function useNavManifest(): {
  items: NavManifestItem[] | null
  failed: boolean
} {
  const [items, setItems] = useState<NavManifestItem[] | null>(cache)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (items) return
    let live = true
    void load()
      .then((loaded) => {
        if (live) setItems(loaded)
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [items])

  return { items, failed }
}

/**
 * The page a route is, by its last segment.
 *
 * The manifest holds route TEMPLATES with `{orgId}` and `{serial}` left
 * in, so a full-string match never works. The leaf is unique across the
 * index, which is also how `find-connect-page` gets away with it.
 */
export function pageFor(
  items: readonly NavManifestItem[],
  pathname: string,
): NavManifestItem | null {
  const leaf = (p: string) => p.split('/').filter(Boolean).pop() ?? ''
  const current = leaf(pathname)
  return items.find((item) => leaf(item.path) === current) ?? null
}

/**
 * The friendly path: organisation, then venue, then where the page sits.
 *
 * `/03126055-361f-11ea-.../venues/FI2EOLUNB3EZ/bookings/day-planner-fast`
 * is a true answer to "which page am I on" and a useless one. The names
 * are what somebody recognises, and the literal route is kept alongside
 * rather than replaced, because it is what you paste into a bug report.
 */
export function friendlyTrail(
  items: readonly NavManifestItem[] | null,
  pathname: string,
  names: { org?: string; venue?: string },
): string[] {
  const trail: string[] = []
  if (names.org) trail.push(names.org)
  if (names.venue) trail.push(names.venue)
  const page = items ? pageFor(items, pathname) : null
  if (page) trail.push(...page.breadcrumb)
  return trail
}
